<script setup lang="ts">
import type { Overlay, OverlayOverride } from '@/types/overlay';

defineOptions({
  name: 'MopOverlay',
});

const props = defineProps({
  overlay: {
    type: Object as PropType<Overlay>,
    required: true,
  },
});

const overlayElementRef: Ref<HTMLElement | null> = ref(null);
const dynamicElementRef: Ref<any | null> = ref(null);
const { closeAll, activeOverlayRef } = useMopOverlay();
const showClose = ref(false);
const isLoadingRef = ref(false);
const isOpenRef = ref(false);
const enableTransitionRef = ref(true);

const overlay: Overlay = props.overlay;
const componentName = resolveComponent(overlay.componentName);
const overlayClasslist = [`overlay--${overlay.componentName.toLowerCase()}`, `overlay--${overlay.type.toLowerCase()}`];

overlay.overlayElementRef = overlayElementRef;

// to simplify logic only loading and showClose state can be emitted from loaded component and / or context override
// the rest need to be provided during open in the overrides
// context.emit('overlay-override', { loading?: boolean, showClose?: boolean });
const overrideStates = (override: OverlayOverride) => {
  if (!override) {
    return;
  }

  if (override.showClose !== undefined) {
    showClose.value = override.showClose;
  }

  if (override.loading !== undefined) {
    isLoadingRef.value = override.loading;
  }

  if (override.enableTransition !== undefined) {
    enableTransitionRef.value = override.enableTransition;
  }
};

onMounted(() => {
  watch(
    () => overlay,
    (overlay) => {
      nextTick(() =>
        setTimeout(() => {
          if (overlay.isOpen && activeOverlayRef.value?.componentKey === overlay.componentKey) {
            overrideStates(overlay.override);
            isOpenRef.value = true;
            if (overlay.override.lockScroll) {
              enableLockScroll();
            }
          } else {
            enableTransitionRef.value = true;
            isOpenRef.value = false;
            if (overlay.override.lockScroll) {
              release();
            }
          }
        }, 10),
      );
    },
    { deep: true },
  );
});

function enableLockScroll() {
  // Make sure, element is in the dom
  setTimeout(() => {
    if (!dynamicElementRef.value || !overlay.isOpen || activeOverlayRef.value?.componentKey !== overlay.componentKey) {
      return;
    }
    const lockElement: HTMLElement | null =
      typeof overlay.override.lockScroll !== 'string'
        ? overlayElementRef.value
        : dynamicElementRef.value.$el.querySelector(overlay.override.lockScroll);
    const preserveOverlayHistory: boolean =
      activeOverlayRef.value?.override?.persist !== true && !activeOverlayRef.value?.override?.persistRoutes?.length;
    lock(lockElement || overlayElementRef.value, preserveOverlayHistory);
  }, 100);
}

const componentProps = overlay.props || {};
const componentKey = overlay.componentKey;

function handleOverlayScrollTop(): void {
  if (overlayElementRef.value) {
    overlayElementRef.value.scrollTop = 0;
  }
}

function handleOverlayScrollBack(scrollBack: number): void {
  if (!scrollBack || typeof scrollBack !== 'number') {
    scrollBack = 0;
  }
  if (overlayElementRef.value) {
    overlayElementRef.value.scrollTop = scrollBack;
  }
}
</script>

<template>
  <div
    ref="overlayElementRef"
    class="overlay"
    :class="[
      'overlay',
      ...overlayClasslist,
      {
        'overlay--loading': isLoadingRef,
        'overlay--opened': isOpenRef,
        'overlay--transition': enableTransitionRef,
      },
    ]"
  >
    <div v-show="showClose" class="overlay__actions">
      <button class="overlay__action-close icon icon--x" @click="closeAll" />
    </div>
    <div class="overlay__body">
      <i v-show="isLoadingRef" class="icon icon--loading-asset" />
      <component
        :is="componentName"
        v-show="!isLoadingRef"
        ref="dynamicElementRef"
        :key="componentKey"
        v-bind="componentProps"
        @overlay-override="overrideStates"
        @overlay-scrolltop="handleOverlayScrollTop"
        @overlay-scrollback="handleOverlayScrollBack"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
$backgroundshadow: rgba(0, 0, 0, 0.5);
$transition-time: 0.15s; // has to match useOverlay timeout

.overlay__actions {
  @include z(global, content);

  display: flex;
  justify-content: flex-end;
  align-items: center;
  height: $overlay-layer-action-height;
  width: 100%;
}

.overlay__action-close {
  border: 0;
  background: transparent;
  cursor: pointer;
  min-width: $space40;
  min-height: $space40;
  display: flex;
  justify-content: center;
  align-items: center;
}

.overlay {
  @include z(layers, above-shadow);
  @include webkit-overflow-scrolling;

  pointer-events: none;
  opacity: 0;
  position: fixed;
  transition: transform ease $transition-time;
  background-color: $white;
  overflow: auto;

  @include apply-print {
    display: none;
  }
}

.overlay--opened {
  opacity: 1;
  pointer-events: all;
}

.overlay__body {
  height: 100%;
}

.overlay--loading {
  .overlay__body {
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
    min-height: $space100;
  }
}

.overlay--fullscreen {
  @include z(layers, fullscreen-layer);

  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  transform: translateY(120%);

  &.overlay--opened {
    transform: translateY(0);
  }
}

.overlay--bottom {
  @include z(layers, above-layer);

  right: 0;
  bottom: 0;
  left: 0;
  max-height: 0;
  transform: translateY(120%);
  transition: all $transition-time ease;

  @include apply-upto(small) {
    max-height: calc(#{$ui-viewport-height} - #{$sticky-small-header-height + 1});
    top: $sticky-small-header-height + 1;

    .wrapper--super-banner & {
      max-height: calc(#{$ui-viewport-height} - #{$v2-superbanner-height + $sticky-small-header-height + 1});
      top: $v2-superbanner-height + $sticky-small-header-height + 1;
    }
  }

  &.overlay--opened {
    transform: translateY(0);
    max-height: $ui-viewport-height;
  }
}

.overlay--left {
  top: 0;
  left: 0;
  width: 50%;
  transform: translateX(-100%);
  height: $ui-viewport-height;
  opacity: 1;
  overflow-y: auto;
  overflow-x: hidden;

  &.overlay--opened {
    transform: translateX(0);
  }
}

.overlay--right {
  top: 0;
  right: 0;
  width: 50%;
  transform: translateX(100%);
  height: $ui-viewport-height;
  opacity: 1;
  overflow-y: auto;
  overflow-x: hidden;

  &.overlay--opened {
    transform: translateX(0);
  }
}

.overlay--top {
  top: 0;
  right: 0;
  left: 0;
  max-height: 0;
  transform: translateY(-120%);

  &.overlay--opened {
    max-height: 2000px;
    transform: translateY(0);
  }

  &.overlay--transition {
    transition: all $transition-time ease;
  }
}

/* Component scpecific position overrides */

.overlay--centered {
  @include z(layers, fullscreen-layer);

  left: 50%;
  max-height: calc(#{$ui-viewport-height} - #{$sticky-big-header-height + $space20});
  max-width: columns(24);
  overflow: auto;
  top: $sticky-big-header-height;
  transform: translateX(-50%);
  width: columns(24);

  @include apply-upto(infinite) {
    /**
       * Because of rule "Expected @include with a block to come before rule (order/order)" we need an "infinite"
       * breakpoint here to keep the order. Otherwise rules in here would have no effect.
       */

    .wrapper--super-banner & {
      max-height: calc(#{$ui-viewport-height} - #{$v2-superbanner-height + $sticky-big-header-height + $space20});
      top: $v2-superbanner-height + $sticky-big-header-height;
    }
  }

  @include apply-upto(medium) {
    max-height: calc(#{$ui-viewport-height} - #{$sticky-small-header-height + $space20});
    top: $sticky-small-header-height;

    .wrapper--super-banner & {
      max-height: calc(#{$ui-viewport-height} - #{$v2-superbanner-height + $sticky-small-header-height + $space20});
      top: $v2-superbanner-height + $sticky-small-header-height;
    }
  }

  @include apply-upto(small) {
    max-width: 100%;
    width: 100%;
    bottom: 0;
    top: $sticky-small-header-height + 1;
    max-height: none;

    .wrapper--super-banner & {
      top: $v2-superbanner-height + $sticky-small-header-height + 1;
      max-height: none;
    }
  }
}

.overlay--categorynavigationoverlay {
  @include z(layers, cart);

  width: 320px;
  top: $header-height;
  height: auto;
  bottom: 0;
  padding-left: $space10;

  @include apply-upto(medium) {
    display: none;
  }

  .overlay__actions {
    position: absolute;
    width: 50px;
    height: 40px;
    right: 0;
  }

  .overlay__action-close {
    cursor: pointer;
    width: 30px;
    height: 30px;
    margin-top: -4px;

    &::after {
      font-size: 40px;
    }
  }

  .overlay__body {
    height: 100%;
  }
}

.overlay--mopheadermobilemenuoverlay {
  position: fixed;
  top: 0;
  bottom: 0;
  width: 320px;
  height: $ui-viewport-height;
  overflow-y: auto;
  overflow-x: hidden;

  .overlay__body {
    padding-top: $mobile-header-height;
  }

  .overlay__actions {
    display: none;
  }
}

.overlay--mopheadermenuoverlay {
  position: fixed;
  top: 0;
  bottom: 0;
  width: 320px;
  height: $ui-viewport-height;
  overflow-y: auto;
  overflow-x: hidden;
  padding-top: 75px;

  .overlay__body {
    height: auto;
  }

  .overlay__actions {
    position: absolute;
  }
}

// Top positioning controlled from MopHeader.vue
.overlay--mopheadersearchsuggestionsoverlay {
  transform: translateY(0%);
  background: $white;
  top: $header-height;

  @include apply-only-touch-device {
    top: $mobile-header-height;
  }

  @include apply-upto(small) {
    top: $mobile-header-height;
  }
}

.overlay--jobfiltersoverlay {
  background: $white;
  top: $header-height;
  height: calc(#{$ui-viewport-height} - #{$header-height});

  @include apply-only-touch-device {
    top: $mobile-header-height;
    height: calc(#{$ui-viewport-height} - #{$mobile-header-height});
  }

  @include apply-upto(small) {
    top: $mobile-header-height;
    height: calc(#{$ui-viewport-height} - #{$mobile-header-height});
    width: 100%;
  }

  .overlay__actions {
    position: absolute;
    width: 50px;
    right: 0;
    height: 20px;
  }
}
</style>
